import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

export interface ContainerScrollOptions {
  readonly isLoaded?: boolean;
  readonly scrollRef: RefObject<HTMLElement | null>;
  readonly storageKey?: string;
}

const MAX_LAYOUT_FRAMES = 60;
const CANCEL_EVENTS = [
  'wheel',
  'touchstart',
  'pointerdown',
  'keydown',
] as const;

export function useContainerScrollRestoration(
  options: ContainerScrollOptions | undefined,
  isBack: boolean
) {
  const scrollRef = options?.scrollRef;
  const isLoaded = options?.isLoaded ?? true;
  const storageKey = options?.storageKey ?? 'container';
  const restoringRef = useRef(false);
  const restored = useRef<{ element: HTMLElement; key: string } | null>(null);

  useEffect(() => {
    const element = scrollRef?.current;
    if (!element || !isLoaded) return undefined;
    const key = `scrollPos:${window.location.pathname}${window.location.search}:${storageKey}`;
    let frame = 0;
    let attempts = 0;
    const completed = () => {
      restored.current = { element, key };
      restoringRef.current = false;
    };
    const cancel = () => {
      cancelAnimationFrame(frame);
      completed();
    };
    const save = () => {
      if (restoringRef.current) return;
      try {
        sessionStorage.setItem(key, String(element.scrollTop));
      } catch (error) {
        if (!(error instanceof window.DOMException)) throw error;
      }
    };
    const pageShow = (event: PageTransitionEvent) => {
      if (event.persisted) cancel();
    };
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem(key);
    } catch (error) {
      if (!(error instanceof window.DOMException)) throw error;
    }
    const target = saved === null ? NaN : Number(saved);
    if (
      isBack &&
      Number.isFinite(target) &&
      target >= 0 &&
      (restored.current?.element !== element || restored.current.key !== key)
    ) {
      restoringRef.current = true;
      const restore = () => {
        attempts += 1;
        if (
          element.scrollHeight - element.clientHeight < target &&
          attempts < MAX_LAYOUT_FRAMES
        ) {
          frame = requestAnimationFrame(restore);
          return;
        }
        element.scrollTop = target;
        restored.current = { element, key };
        frame = requestAnimationFrame(completed);
      };
      frame = requestAnimationFrame(restore);
    }

    element.addEventListener('scroll', save, { passive: true });
    CANCEL_EVENTS.forEach(event =>
      document.addEventListener(event, cancel, { capture: true, passive: true })
    );
    window.addEventListener('pagehide', save);
    window.addEventListener('pageshow', pageShow);
    return () => {
      cancelAnimationFrame(frame);
      restoringRef.current = false;
      element.removeEventListener('scroll', save);
      CANCEL_EVENTS.forEach(event =>
        document.removeEventListener(event, cancel, true)
      );
      window.removeEventListener('pagehide', save);
      window.removeEventListener('pageshow', pageShow);
    };
  }, [scrollRef, isLoaded, storageKey, isBack]);

  return { restoringRef };
}
